import { useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Check,
  ContentCopy,
  ExpandLess,
  ExpandMore,
  Search,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import { roles as ROLE_CONSTANTS } from "../utils";
import { useCopyFeedback } from "./useCopyFeedback";
import { sentenceCase, stripEmailDomain } from "./utils";
import { membersStyles as styles, detailRowStyles } from "./styles";

// Owner -> Lead -> Developer, matching the order roles are declared in.
const ROLE_ORDER = Object.keys(ROLE_CONSTANTS);

const roleLabel = (role, count) => {
  const word = sentenceCase(role).toLowerCase();
  return `${word}${count === 1 ? "" : "s"}`;
};

// One member row — its own hook instance so the copy icon's inline feedback
// is independent per row.
const MemberRow = ({ email, isYou }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const { state: copyState, copy } = useCopyFeedback();

  return (
    <Box sx={styles.memberItem(colors)}>
      <span title={email}>{stripEmailDomain(email)}</span>
      {isYou && (
        <Box component="span" sx={styles.memberYou(colors)}>
          you
        </Box>
      )}
      <Box
        className="member-copy"
        component="span"
        sx={{
          ...styles.memberCopyIcon(colors),
          ...(copyState === "copied" && {
            opacity: 1,
            color: colors.green[400],
          }),
        }}
        onClick={() => copy(email)}
      >
        {copyState === "copied" ? (
          <Check sx={{ fontSize: "13px" }} />
        ) : (
          <ContentCopy sx={{ fontSize: "13px", cursor: "pointer" }} />
        )}
      </Box>
    </Box>
  );
};

// Lives inside Endpoints & access — the Members row itself, its VIEW/HIDE
// toggle, search, and the grouped/ungrouped list.
const MembersSection = ({ row, currentUserEmail }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const { state: copyAllState, copy: copyAll } = useCopyFeedback();

  const memberList = useMemo(() => {
    const emails = row?.indexedMembers || [];
    return emails.map((email) => ({ email, role: row?.members?.[email] }));
  }, [row]);

  const distinctRoles = useMemo(
    () => ROLE_ORDER.filter((role) => memberList.some((m) => m.role === role)),
    [memberList]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return memberList;
    return memberList.filter(
      (m) =>
        m.email?.toLowerCase().includes(q) ||
        stripEmailDomain(m.email)?.toLowerCase().includes(q)
    );
  }, [memberList, query]);

  if (!memberList.length) return null;

  const summary =
    distinctRoles.length <= 1
      ? `${memberList.length} ${roleLabel(
          distinctRoles[0] || memberList[0]?.role,
          memberList.length
        )}`
      : `${memberList.length} · ${distinctRoles.length} roles`;

  const copyAllLabel =
    copyAllState === "copied"
      ? "Copied"
      : copyAllState === "error"
      ? "Couldn't copy"
      : "Copy all";

  const renderMember = (member) => {
    const isYou =
      currentUserEmail &&
      member.email?.toLowerCase() === currentUserEmail.toLowerCase();
    return <MemberRow key={member.email} email={member.email} isYou={isYou} />;
  };

  return (
    <Box sx={detailRowStyles.row(colors)}>
      <Box sx={detailRowStyles.label(colors)}>Members</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={detailRowStyles.valueGroup}>
          <Typography sx={detailRowStyles.value(colors)}>{summary}</Typography>
          {memberList.length > 1 && (
            <Box
              component="button"
              type="button"
              sx={styles.toggle(colors)}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? "Hide" : "View"}
              {expanded ? (
                <ExpandLess sx={{ fontSize: "14px" }} />
              ) : (
                <ExpandMore sx={{ fontSize: "14px" }} />
              )}
            </Box>
          )}
        </Box>

        {expanded && (
          <Box sx={styles.expanded(colors)}>
            <Box sx={styles.toolbar}>
              <TextField
                size="small"
                placeholder="Search members"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={styles.searchField(colors)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        sx={{ fontSize: "15px", color: colors.grey[400] }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                component="button"
                type="button"
                sx={styles.copyAll(colors)}
                onClick={() => copyAll(filtered.map((m) => m.email).join(", "))}
              >
                {copyAllLabel}
              </Box>
            </Box>

            {query.trim() && (
              <Typography sx={styles.summaryLine(colors)}>
                {filtered.length
                  ? `${filtered.length} of ${
                      memberList.length
                    } match "${query.trim()}"`
                  : `No members match "${query.trim()}"`}
              </Typography>
            )}

            {!filtered.length ? null : distinctRoles.length <= 1 ? (
              <>
                <Typography sx={styles.summaryLine(colors)}>
                  <Tooltip title={ROLE_CONSTANTS[distinctRoles[0]] || ""}>
                    <span>
                      All {filtered.length} hold{" "}
                      <Box
                        component="span"
                        sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}
                      >
                        {ROLE_CONSTANTS[distinctRoles[0]] ||
                          memberList[0]?.role}
                      </Box>
                    </span>
                  </Tooltip>
                </Typography>
                <Box sx={styles.list}>{filtered.map(renderMember)}</Box>
              </>
            ) : (
              ROLE_ORDER.filter((role) =>
                filtered.some((m) => m.role === role)
              ).map((role) => {
                const group = filtered.filter((m) => m.role === role);
                return (
                  <Box key={role}>
                    <Tooltip title={ROLE_CONSTANTS[role]}>
                      <Box sx={styles.groupHeader(colors)}>
                        <Typography sx={styles.groupHeaderTitle(colors)}>
                          {sentenceCase(role)}
                        </Typography>
                        <Typography sx={styles.groupHeaderCount(colors)}>
                          {group.length}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Box sx={styles.list}>{group.map(renderMember)}</Box>
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MembersSection;
